/**
 * WorkOS session cookie utilities.
 *
 * The `wos-user` cookie is a non-httpOnly marker set by the backend to signal
 * that a WorkOS session exists. Its value is `{ hasSession: true }` — the
 * frontend only needs to check for the cookie's presence (not its contents).
 */

import { removeCookie, setCookie } from './cookies';
import { SESSION_KEYS, session } from './storage';

const WOS_USER_COOKIE = 'wos-user';
const WORKOS_OVERRIDE_COOKIE = 'workos-override';

/** The domain used for cross-subdomain SSO cookies in production. */
const COOKIE_DOMAIN = '.lifemd.com';

const isLifeMDDomain = (): boolean => window.location.hostname.endsWith('.lifemd.com');

const hasCookie = (name: string): boolean =>
  document.cookie.split(';').some((c) => c.trim().startsWith(`${name}=`));

export const hasWorkOSSession = (): boolean => hasCookie(WOS_USER_COOKIE);

export const hasWorkOSOverride = (): boolean => hasCookie(WORKOS_OVERRIDE_COOKIE);

export const setWorkOSOverrideCookie = (): void => {
  setCookie(WORKOS_OVERRIDE_COOKIE, 'true', { path: '/' });
};

/**
 * Clears all WorkOS-related cookies and sessionStorage entries.
 * Handles both local development (no domain) and production (.lifemd.com) cookies.
 */
export const clearWorkOSCookies = (): void => {
  const domain = isLifeMDDomain() ? COOKIE_DOMAIN : undefined;
  removeCookie(WOS_USER_COOKIE, { ...(domain && { domain }), path: '/' });
  removeCookie(WORKOS_OVERRIDE_COOKIE, { path: '/' });
  // Via `session` rather than the global: where site data is blocked, `sessionStorage` throws on
  // the property read *and* on the call, and this runs between `clearUser` and `setUser` on the
  // ordinary code sign-in — a throw here signs the patient out and spends their code in silence.
  session.remove(SESSION_KEYS.SOCKET_ACCESS_TOKEN);
};
